import { useEffect, useState } from "react";
import { PanelShell, Badge, StatChip, tokens } from "../index";
import {
  createInitialLivePlaybackState, buildLiveConductorState, DEFAULT_CONDUCTOR_OPTIONS,
  reduceLivePlaybackState, applyClockTick,
} from "@djsem/live-core";
import type { DJSEMLiveEventEnvelope, LivePlaybackState } from "@djsem/live-core";
import { buildLivePerformanceRenderState } from "@djsem/render-state";
import type { DJSEMLiveHealth, DJSEMPlatform } from "../platform";

const HEARTBEAT_TIMEOUT_MS = 2000;
const CLOCK_TICK_MS = 1000;

/**
 * Live module — the desktop live-performance panel: bridge/cache status, discovered DJ Link
 * devices, decks, and the live conductor state, all driven by real beat-link envelopes
 * forwarded from the Electron bridge. On web (no live bridge) it shows an explicit
 * desktop-only notice rather than a "disconnected" state (the one place web and desktop
 * legitimately differ).
 *
 * @param health - The current live bridge/cloud health snapshot, or null if unknown.
 * @param cachedCount - Number of cached offline venue packages available.
 * @param liveAvailable - Whether the live bridge is available on this platform at all.
 * @param platform - The current platform; used to subscribe to real live events on desktop.
 * @returns A desktop-only notice on web, or bridge/device/deck/conductor status panels.
 */
export function LiveModule({ health, cachedCount, liveAvailable, platform }: { health: DJSEMLiveHealth | null; cachedCount: number; liveAvailable: boolean; platform: DJSEMPlatform }) {
  const [live, setLive] = useState<LivePlaybackState>(() => createInitialLivePlaybackState(Date.now()));

  // Subscribe to real beat-link envelopes forwarded from the Electron bridge and fold them
  // through the pure reducer; re-derive connection quality on a clock tick so a stalled
  // bridge (no heartbeats) degrades even between events. No-op on web (null live bridge).
  useEffect(() => {
    if (!liveAvailable || !platform.live.available) return;
    const unsubscribe = platform.live.onLiveEvent((envelope) => {
      setLive((prev) => reduceLivePlaybackState(prev, envelope as DJSEMLiveEventEnvelope, { heartbeatTimeoutMs: HEARTBEAT_TIMEOUT_MS }));
    });
    const tick = setInterval(() => {
      setLive((prev) => applyClockTick(prev, Date.now(), { heartbeatTimeoutMs: HEARTBEAT_TIMEOUT_MS }));
    }, CLOCK_TICK_MS);
    return () => { unsubscribe(); clearInterval(tick); };
  }, [liveAvailable, platform.live]);

  // On web there is no desktop live bridge — say so clearly and intentionally, rather than
  // showing "disconnected" which reads as broken. This is the ONE place web and desktop
  // legitimately differ, so the copy makes the difference feel designed, not accidental.
  if (!liveAvailable) {
    return (
      <PanelShell title="Live conduct">
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 560 }}>
          <div style={{ fontSize: tokens.text.lg, color: tokens.color.text }}>
            Live conduct runs in the DJSEM desktop app.
          </div>
          <p style={{ margin: 0, color: tokens.color.textDim, fontSize: tokens.text.md, lineHeight: 1.55 }}>
            The browser app covers your whole prep workflow — library, sets, visuals, brand,
            releases, campaigns and gigs. Performing a show live (bridge to CDJs, rehearsal
            playback, offline venue packs) needs the desktop Studio, which connects to the local
            live bridge. Everything you prepare here syncs to desktop automatically.
          </p>
          <div style={{ marginTop: 4 }}>
            <Badge tone="neutral">Desktop feature</Badge>
          </div>
        </div>
      </PanelShell>
    );
  }
  const conductor = buildLiveConductorState(live, null, DEFAULT_CONDUCTOR_OPTIONS);
  const render = conductor.outputFrame ? buildLivePerformanceRenderState(conductor.outputFrame) : null;
  const bridgeLabel = health ? (health.bridgeHealthy ? "healthy" : health.bridgeConnected ? "no heartbeat" : "disconnected") : "—";
  const bridgeTone = health?.bridgeHealthy ? "ok" : "warn";
  const devices = Object.values(live.devices);
  const decks = Object.values(live.decks);
  return (
    <>
      <PanelShell title="Bridge & cache">
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <StatChip label="Bridge" value={bridgeLabel} />
          <StatChip label="Cloud" value={health?.cloudReachable ? "reachable" : "cache only"} />
          <StatChip label="Cached packages" value={String(cachedCount)} />
        </div>
        <div style={{ marginTop: 10 }}><Badge tone={bridgeTone}>{bridgeLabel}</Badge></div>
      </PanelShell>
      <PanelShell title="Devices" style={{ marginTop: 12 }}>
        {devices.length === 0 ? (
          <p style={{ margin: 0, color: tokens.color.textDim, fontSize: tokens.text.sm }}>
            No DJ Link devices discovered yet. Power on CDJs/XDJs on the same network as this machine.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {devices.map((d) => (
              <div key={d.deviceId} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <Badge tone={d.status === "online" ? "ok" : "neutral"}>{d.status}</Badge>
                <span style={{ color: tokens.color.text, fontSize: tokens.text.sm }}>{d.name ?? `Device ${d.deviceId}`}</span>
                <span style={{ color: tokens.color.textDim, fontSize: tokens.text.xs }}>
                  {d.deviceType}{d.ipAddress ? ` · ${d.ipAddress}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </PanelShell>
      <PanelShell title="Decks" style={{ marginTop: 12 }}>
        {decks.length === 0 ? (
          <p style={{ margin: 0, color: tokens.color.textDim, fontSize: tokens.text.sm }}>
            No decks reporting yet.
          </p>
        ) : (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {decks.map((d) => (
              <div key={d.deckId} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <StatChip label={d.deckId} value={d.status} />
                <span style={{ color: tokens.color.text, fontSize: tokens.text.sm }}>
                  {d.track?.title ? `${d.track.title}${d.track.artist ? ` — ${d.track.artist}` : ""}` : "—"}
                </span>
                <span style={{ color: tokens.color.textDim, fontSize: tokens.text.xs }}>
                  {d.playback.bpm ? `${d.playback.bpm.toFixed(1)} BPM` : ""}
                  {d.playback.tempoMaster ? " · master" : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </PanelShell>
      <PanelShell title="Conductor" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <StatChip label="Mode" value={conductor.mode} />
          <StatChip label="Safe mode" value={conductor.safety.safeModeActive ? "active" : "off"} />
          <StatChip label="LED intensity" value={render ? render.ledWall.intensity.toFixed(2) : "—"} />
        </div>
        <p style={{ margin: "10px 0 0", color: tokens.color.muted, fontSize: tokens.text.sm }}>
          Fallback visual mode: {conductor.safety.fallbackVisualMode}. Simulation only — no physical
          hardware output.
        </p>
      </PanelShell>
    </>
  );
}
